import C from "@/components/sidebar/StandardSidebar.module.css";

import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { PriceBlock } from "@/components/sidebar/PriceBlock.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { serviceLink } from "@/modules/services/links.ts";
import { ServiceOsImageIcon } from "@/modules/services/os/ServiceOsImageIcon.tsx";
import { cn } from "@/utils/classNames.ts";
import { datetimeToDate } from "@warrenio/api-spec/conversion";

import { EstimatedMonthlyCostRange } from "@/components/EstimatedMonthlyCostRange.tsx";
import type { KubernetesPrice } from "@/modules/pricing/resourcePricing.ts";
import { availableServicesAtom, type ManagedServiceWithAssigned } from "@/modules/services/joinAssignedQuery.ts";
import type { ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ManagedServicesVmBody } from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai";

const sortFields = [
    {
        id: "display_name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => i.display_name),
    },
    { id: "created_at", title: <Trans>Created date</Trans>, getValue: (i) => datetimeToDate(i.created_at).getTime() },
    { id: "location", title: <Trans>Location</Trans>, getValue: (i) => i.properties.location },
] satisfies SimpleSortField<ManagedServiceLoc>[];

export default function ServicesList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <ServicesData {...props} />}
            title={t`Services`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: ResourceCreateLinks.managed_service,
            }}
            listId="services"
        />
    );
}

function ServicesData({ queryString, sortId, sortAsc }: SidebarListState) {
    const query = useAtomValue(availableServicesAtom);

    let serviceItems = getFilteredItems(queryString, [...(query.data?.values() ?? [])], (item) => [
        item.display_name,
        item.uuid,
    ]);
    serviceItems = getSortedItems(sortFields, { sortId, sortAsc }, serviceItems);

    return (
        <ListResult
            items={serviceItems.map((obj) => (
                <ServiceBlock key={obj.uuid} item={obj} />
            ))}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-services-icon">
                    <Trans>Create a new service and get it running in minutes.</Trans>
                </EmptyItem>
            }
        />
    );
}

export function ServiceBlock({ item }: { item: ManagedServiceWithAssigned }) {
    const { display_name, service, resources, properties, assignedPublicIp, billing_account_id } = item;

    const itemStatus = (() => {
        if (service === "kubernetes") {
            const { spec, status } = properties ?? {};
            const { hibernation } = spec ?? {};
            if (hibernation && status) {
                if (hibernation.enabled && status.hibernated) {
                    return (
                        <div className="color-muted text-uppercase">
                            <Trans>Hibernated</Trans>
                        </div>
                    );
                }

                if (hibernation.enabled && !status.hibernated) {
                    return (
                        <div className="color-muted text-uppercase">
                            <Trans>Hibernating</Trans>
                        </div>
                    );
                }

                if (!hibernation.enabled && status.hibernated) {
                    return (
                        <div className="color-muted text-uppercase">
                            <Trans>Waking up</Trans>
                        </div>
                    );
                }
            }
        }

        let runningVMs = 0;
        let stoppedVMs = 0;

        const vms = resources.filter((res: ManagedServicesVmBody) => res.resource_type === "vm");
        for (const { resource_allocation } of vms) {
            if (resource_allocation?.status === "running") {
                runningVMs++;
            }
            if (resource_allocation && resource_allocation.status !== "running") {
                stoppedVMs++;
            }
        }

        return (
            <div>
                {runningVMs > 0 && (
                    <div className="text-success text-uppercase">
                        {runningVMs} <Trans>Running</Trans>
                    </div>
                )}
                {stoppedVMs > 0 && (
                    <div className="text-error text-uppercase">
                        {stoppedVMs} <Trans>Stopped</Trans>
                    </div>
                )}
            </div>
        );
    })();

    const ip = assignedPublicIp?.address ?? ("service_ip" in properties ? properties.service_ip : undefined);

    return (
        <StandardBlock link={serviceLink(item)}>
            <div className={C.StandardBlockIcon}>
                <ServiceOsImageIcon os_name={service} className="size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{display_name}</b>
                <div className="text-ellipsis color-primary">
                    {service === "kubernetes" ? (
                        properties.location
                    ) : (
                        <>
                            {ip} {!!properties.location && <> / {properties.location}</>}
                        </>
                    )}
                </div>
            </div>

            <div className={C.StandardBlockType}>{itemStatus}</div>

            {service === "kubernetes" ? (
                <KubernetesPriceBlock price={item.price as KubernetesPrice} />
            ) : (
                <PriceBlock price={item.price} billingAccountId={billing_account_id} />
            )}
        </StandardBlock>
    );
}

function KubernetesPriceBlock({ price, billingAccountId }: { price: KubernetesPrice; billingAccountId?: number }) {
    return (
        <div className={C.StandardBlockInfo}>
            <div className="text-muted">
                <Trans>Monthly cost</Trans>
            </div>
            <Trans>from</Trans>{" "}
            <EstimatedMonthlyCostRange
                minimum={price.minimum}
                maximum={price.maximum}
                billingAccountId={billingAccountId}
            />
        </div>
    );
}
